import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // The tenant every request is scoped to. It lives in localStorage because
  // config/api.js reads it there for the X-Tenant-ID header, but it is mirrored
  // in state as well: the nav has to change the moment a super admin opens or
  // leaves a business, and localStorage does not re-render anything.
  const [tenantScope, setTenantScope] = useState(() => ({
    tenantId: localStorage.getItem('tenantId'),
    tenantName: localStorage.getItem('tenantName')
  }));

  // One writer for both copies, so they cannot drift apart
  const storeTenantScope = useCallback((tenantId, tenantName) => {
    if (tenantId) {
      localStorage.setItem('tenantId', tenantId);
      if (tenantName) {
        localStorage.setItem('tenantName', tenantName);
      } else {
        localStorage.removeItem('tenantName');
      }
    } else {
      localStorage.removeItem('tenantId');
      localStorage.removeItem('tenantName');
    }
    setTenantScope({ tenantId: tenantId || null, tenantName: tenantName || null });
  }, []);

  // Defined above the init effect on purpose: that effect calls it when a
  // stored token turns out to be dead, and a dependency cannot be listed before
  // it exists.
  const logout = useCallback(() => {
    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    storeTenantScope(null);

    // Reset state
    setUser(null);
    setIsAuthenticated(false);
  }, [storeTenantScope]);

  // Initialize authentication state
  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');

        if (token && storedUser) {
          const userData = JSON.parse(storedUser);
          
          // Set tenant header only if user has tenantId (not for super_admin).
          // A super admin's scope is deliberately left alone here - they may
          // have opened a business and then refreshed the page, and re-reading
          // it from storage is what keeps them where they were.
          if (userData.tenantId) {
            storeTenantScope(userData.tenantId, userData.tenantName);
          }

          // Verify token is still valid
          try {
            const response = await api.get('/api/auth/me');
            setUser(response.data.user);
            setIsAuthenticated(true);
          } catch (error) {
            // Token is invalid, clear storage
            logout();
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [logout, storeTenantScope]);

  const login = (userData, token) => {
    setUser(userData);
    setIsAuthenticated(true);
    
    // Ensure token and user data are stored in localStorage
    if (token) {
      localStorage.setItem('token', token);
    }
    localStorage.setItem('user', JSON.stringify(userData));
    
    // Set tenant ID if user has one. Clearing it matters as much as setting it:
    // a super admin who scoped into a shop and closed the tab leaves the id
    // behind, and the next person to sign in on that browser - a sales agent,
    // say - would carry it into every request they make.
    storeTenantScope(userData.tenantId, userData.tenantName);
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));

    // Update tenant header if changed
    if (userData.tenantId) {
      storeTenantScope(userData.tenantId, userData.tenantName);
    }
  };

  // Method for super admin to set tenant ID. The name is optional and only
  // feeds the "Viewing X" banner - passing the id alone still scopes correctly.
  const setTenantId = (tenantId, tenantName) => {
    if (user && user.role === 'super_admin') {
      // The api instance picks the id up from localStorage on every request
      storeTenantScope(tenantId, tenantName);
    }
  };

  // Get current tenant ID
  const getCurrentTenantId = () => {
    return tenantScope.tenantId;
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    updateUser,
    setTenantId,
    getCurrentTenantId,
    // Reactive copies - use these when rendering, so the UI follows the scope
    tenantId: tenantScope.tenantId,
    tenantName: tenantScope.tenantName
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};